import React, { useEffect, useMemo, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Divider,
  TextField,
  Box,
  Paper,
  Chip,
  InputAdornment,
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import EditIcon from "@material-ui/icons/Edit";
import CancelIcon from "@material-ui/icons/Cancel";

import ReactDragListView from "react-drag-listview/lib/index.js";

import { updatePermissionOrderMutation } from "./ModulesGqlQuery";
import { useMutation } from "@apollo/client";

export default function FunctionalityPermissionSettingDialog({
  openConnectDialog,
  exitAddModal,
  onSuccess,
  permissionsList,
  moduleFunctionality
}) {
  const [permissionList, setPermissionList] = useState([]);

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [isGroupFormOpen, setIsGroupFormOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [editingPermissionId, setEditingPermissionId] = useState(null);
  const [editingPermission, setEditingPermission] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const [isNewPermissionEditing, setIsNewPermissionEditing] = useState(false);

  const [updatePermissionOrder] = useMutation(updatePermissionOrderMutation);

  useEffect(() => {
    setErrorMessage(null);

    if (!openConnectDialog) {
      return;
    }

    if (Array.isArray(permissionsList)) {
      setPermissionList(
        permissionsList.map(function(item) {
          return {
            ...item,
          };
        }),
      );
    } else {
      setPermissionList([]);
    }

    setSearchText("");
    setNewGroupName("");
    setNewGroupDescription("");
    setIsGroupFormOpen(false);
    setEditingGroupId(null);
    setEditingGroup(null);
    setEditingPermissionId(null);
    setEditingPermission(null);
    setIsNewPermissionEditing(false);
  }, [permissionsList, openConnectDialog]);

  const visiblePermissions = useMemo(
    function() {
      return permissionList.filter(function(item) {
        return !item.isRemove;
      });
    },
    [permissionList],
  );

  const filteredPermissions = useMemo(
    function() {
      const search = searchText.trim().toLowerCase();

      if (!search) {
        return visiblePermissions;
      }

      return visiblePermissions.filter(function(item) {
        const name = item.permissionDisplayName || "";
        const description = item.description || "";
        const permissionName = item.permissionName || "";
        const permissionCode = item.permissionCode || "";
        const moduleName = item.moduleFunctionalityName || "";

        return (
          name.toLowerCase().includes(search) ||
          description.toLowerCase().includes(search) ||
          permissionName.toLowerCase().includes(search) ||
          permissionCode.toLowerCase().includes(search) ||
          moduleName.toLowerCase().includes(search)
        );
      });
    },
    [visiblePermissions, searchText],
  );

  const groupCount = useMemo(
    function() {
      return visiblePermissions.filter(function(item) {
        return item.permissionName === "NEW_LINE";
      }).length;
    },
    [visiblePermissions],
  );

  const permissionCount = useMemo(
    function() {
      return visiblePermissions.filter(function(item) {
        return item.permissionName !== "NEW_LINE";
      }).length;
    },
    [visiblePermissions],
  );

  const handleEditPermission = function(permission) {
    const editId =
      permission.id !== null && permission.id !== undefined
        ? permission.id
        : permission.tempId;

    setEditingPermissionId(editId);

    setEditingPermission({
      id: permission.id,
      tempId: permission.tempId,

      permissionDisplayName: permission.permissionDisplayName || "",

      permissionCode: permission.permissionCode || "",

      permissionName: permission.permissionName || "",

      description: permission.description || "",
    });

    setIsNewPermissionEditing(false);
  };

  const handleEditPermissionChange = function(field, value) {
    setEditingPermission(function(previous) {
      return {
        ...previous,
        [field]: value,
      };
    });
  };

  const handleAddPermission = function(group) {
    const tempId = "new-permission-" + Date.now();

    const newPermission = {
      id: null,

      tempId: tempId,

      functionalityLineOrder: group.functionalityLineOrder || 1,

      lineOrder: permissionList.length + 1,

      moduleFunctionalityName: group.moduleFunctionalityName || "",

      permissionName: "",
      permissionDisplayName: "",
      permissionCode: "",
      description: "",

      isAvailable: false,
      isExistInRole: false,
      permit: true,

      isNew: true,
      isRemove: false,
    };

    setPermissionList(function(previousList) {
      const groupIndex = previousList.findIndex(function(item) {
        if (group.id !== null && group.id !== undefined) {
          return item.id === group.id;
        }

        return item === group;
      });

      if (groupIndex === -1) {
        return [...previousList, newPermission];
      }

      const updatedList = [...previousList];

      updatedList.splice(groupIndex + 1, 0, newPermission);

      return updatedList;
    });

    setEditingPermissionId(tempId);

    setEditingPermission({
      id: null,
      tempId: tempId,
      permissionDisplayName: "",
      permissionCode: "",
      permissionName: "",
      description: "",
    });

    setIsNewPermissionEditing(true);
  };

  const handleSavePermission = function(permission) {
    if (!editingPermission) {
      return;
    }

    const displayName = editingPermission.permissionDisplayName
      ? editingPermission.permissionDisplayName.trim()
      : "";

    const permissionCode = editingPermission.permissionCode
      ? editingPermission.permissionCode.trim()
      : "";

    const permissionName = editingPermission.permissionName
      ? editingPermission.permissionName.trim()
      : "";

    const description = editingPermission.description
      ? editingPermission.description.trim()
      : "";

    if (!displayName) {
      return;
    }

    if (!permissionCode) {
      return;
    }

    if (!permissionName) {
      return;
    }

    setPermissionList(function(previousList) {
      return previousList.map(function(item) {
        const existingPermission =
          permission.id !== null &&
          permission.id !== undefined &&
          item.id === permission.id;

        const newPermission =
          permission.id === null &&
          permission.tempId &&
          item.tempId === permission.tempId;

        if (existingPermission || newPermission) {
          return {
            ...item,

            permissionDisplayName: displayName,
            permissionCode: permissionCode,
            permissionName: permissionName,
            description: description,
          };
        }

        return item;
      });
    });

    setEditingPermissionId(null);
    setEditingPermission(null);
    setIsNewPermissionEditing(false);
  };

  const handleCancelEditPermission = function() {
    if (
      isNewPermissionEditing &&
      editingPermission &&
      editingPermission.tempId
    ) {
      setPermissionList(function(previousList) {
        return previousList.filter(function(item) {
          return item.tempId !== editingPermission.tempId;
        });
      });
    }

    setEditingPermissionId(null);
    setEditingPermission(null);
    setIsNewPermissionEditing(false);
  };

  const dragProps = {
    onDragEnd: function(fromIndex, toIndex) {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
        return;
      }

      setPermissionList(function(previousList) {
        const activeItems = previousList.filter(function(item) {
          return !item.isRemove;
        });

        const removedItems = previousList.filter(function(item) {
          return item.isRemove;
        });

        const updatedList = [...activeItems];

        const movedItem = updatedList.splice(fromIndex, 1)[0];

        if (!movedItem) {
          return previousList;
        }

        updatedList.splice(toIndex, 0, movedItem);

        return [...updatedList, ...removedItems];
      });
    },

    nodeSelector: ".permission-row",

    handleSelector: ".drag-handle",
  };

  const handleAddPermissionToTop = function() {
    let firstGroup =
      permissionList.find(function(item) {
        return !item.isRemove;
      }) || null;

    if (!firstGroup) {
        firstGroup = {
          functionalityLineOrder: 1,
          moduleFunctionalityName :moduleFunctionality.moduleFunctionalityName

        };  
    }

    handleAddPermission(firstGroup);
  };

  const handleAddGroup = function() {
    const groupName = newGroupName.trim();

    if (!groupName) {
      return;
    }

    const lastItem =
      permissionList.length > 0
        ? permissionList[permissionList.length - 1]
        : null;

    const newGroup = {
      id: null,

      functionalityLineOrder:
        lastItem && lastItem.functionalityLineOrder
          ? lastItem.functionalityLineOrder
          : 1,

      lineOrder: permissionList.length + 1,

      moduleFunctionalityName:
        permissionList.length > 0 && moduleFunctionality.moduleFunctionalityName
          ? moduleFunctionality.moduleFunctionalityName
          : "",

      permissionName: "NEW_LINE",

      permissionDisplayName: groupName,

      permissionCode: "NEW_LINE",

      description: newGroupDescription.trim(),

      // UI fields
      isAvailable: false,
      isExistInRole: false,
      permit: true,

      isNew: true,
      isRemove: false,
    };

    setPermissionList(function(previousList) {
      return [...previousList, newGroup];
    });

    setNewGroupName("");
    setNewGroupDescription("");
    setIsGroupFormOpen(false);
  };

  const handleEditGroup = function(group) {
    const groupId =
      group.id !== null && group.id !== undefined ? group.id : group.tempId;

    setEditingGroupId(groupId);
    setEditingGroup({
      id: group.id,
      tempId: group.tempId,
      permissionDisplayName: group.permissionDisplayName || "",
      description: group.description || "",
    });
  };

  const handleSaveGroup = function(group) {
    if (!editingGroup) {
      return;
    }

    const groupName = editingGroup.permissionDisplayName.trim();

    if (!groupName) {
      return;
    }

    setPermissionList(function(previousList) {
      return previousList.map(function(item) {
        const sameGroup =
          (group.id !== null &&
            group.id !== undefined &&
            item.id === group.id) ||
          (group.id === null && item.tempId === group.tempId);

        return sameGroup
          ? {
              ...item,
              permissionDisplayName: groupName,
              description: editingGroup.description.trim(),
            }
          : item;
      });
    });

    setEditingGroupId(null);
    setEditingGroup(null);
  };

  const handleRemove = function(permission) {
    setPermissionList(function(previousList) {
      return previousList.map(function(item) {
        const sameExistingItem =
          permission.id !== null &&
          permission.id !== undefined &&
          item.id === permission.id;

        const sameNewItem =
          permission.id === null &&
          permission.tempId &&
          item.tempId === permission.tempId;

        if (sameExistingItem || sameNewItem) {
          return {
            ...item,
            isRemove: true,
          };
        }

        return item;
      });
    });
  };

  const handleUpdate = async function(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (editingPermissionId !== null) {
      if (isNewPermissionEditing && editingPermission) {
        const displayName = editingPermission.permissionDisplayName
          ? editingPermission.permissionDisplayName.trim()
          : "";

        const permissionCode = editingPermission.permissionCode
          ? editingPermission.permissionCode.trim()
          : "";

        const permissionName = editingPermission.permissionName
          ? editingPermission.permissionName.trim()
          : "";

        if (!displayName || !permissionCode || !permissionName) {
          return;
        }
      }

      handleSavePermission(
        permissionList.find(function(item) {
          const itemId =
            item.id !== null && item.id !== undefined ? item.id : item.tempId;

          return itemId === editingPermissionId;
        }),
      );

      return;
    }

    try {
      const updatedPermissions = permissionList
        .filter(function(item) {
          return !item.isRemove;
        })
        .map(function(item, index) {
          const isNewGroup =
            item.isNew === true && item.permissionName === "NEW_LINE";

          return {
            id: item.isNew === true ? null : item.id,

            functionalityLineOrder: item.functionalityLineOrder || 1,

            lineOrder: index + 1,

            moduleFunctionalityName: item.moduleFunctionalityName || "",

            permissionName: isNewGroup ? "NEW_LINE" : item.permissionName || "",

            permissionDisplayName: item.permissionDisplayName || "",

            permissionCode: isNewGroup ? "NEW_LINE" : item.permissionCode || "",

            description: item.description || "",
          };
        });

      updatePermissionOrder({
        variables: {
          permissions: updatedPermissions,
        },
      })
        .then(function() {
          if (onSuccess) {
            onSuccess(updatedPermissions);
          }
        })
        .catch(function(error) {
          if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            setErrorMessage(error.graphQLErrors[0].message);
          }
          console.error("Update permission error:", error);
        });
    } catch (error) {
      console.error("Update permission error:", error);
    }
  };

  const handleCancel = function() {
    setNewGroupName("");
    setNewGroupDescription("");
    setIsGroupFormOpen(false);
    setEditingGroupId(null);
    setEditingGroup(null);
    setSearchText("");
    setEditingPermissionId(null);
    setEditingPermission(null);
    setIsNewPermissionEditing(false);

    if (exitAddModal) {
      exitAddModal();
    }
  };

  const renderPermissionRow = function(permission, index) {
    const isGroup = permission.permissionName === "NEW_LINE";

    const permissionId =
      permission.id !== null && permission.id !== undefined
        ? permission.id
        : permission.tempId;

    const isEditing = !isGroup && editingPermissionId === permissionId;
    const isEditingGroup = isGroup && editingGroupId === permissionId;

    return (
      <Paper
        key={permission.id || permission.tempId || `new-${index}`}
        className="permission-row"
        elevation={0}
        style={{
          marginBottom: 8,

          border: isGroup
            ? "1px solid #d7defc"
            : isEditing
            ? "1px solid #3f51b5"
            : "1px solid #e5e7eb",

          borderRadius: 8,

          backgroundColor: isGroup ? "#f7f8ff" : "#ffffff",
        }}
      >
        <Box
          style={{
            display: "flex",
            alignItems: "center",

            minHeight: isGroup ? 56 : isEditing ? 260 : 68,

            padding: "8px 10px",
          }}
        >
          {/* Drag handle */}
          <Box
            className="drag-handle"
            style={{
              width: 34,
              height: 38,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              color: "#9ca3af",
              cursor: "grab",
              flexShrink: 0,
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>

          <Box
            style={{
              width: 38,
              height: 38,
              borderRadius: 8,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              marginRight: 12,
              flexShrink: 0,

              backgroundColor: isGroup ? "#e9edff" : "#f3f4f6",

              color: isGroup ? "#4f46e5" : "#6b7280",
            }}
          >
            {isGroup ? (
              <FolderOutlinedIcon fontSize="small" />
            ) : (
              <LockOutlinedIcon fontSize="small" />
            )}
          </Box>

          <Box
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {isGroup ? (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  paddingRight: 8,
                }}
              >
                <Typography
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#1f2937",

                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isEditingGroup ? (
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        paddingRight: 10,
                        width: "100%",
                      }}
                    >
                      <TextField
                        size="small"
                        variant="outlined"
                        label="Group Name"
                        value={editingGroup.permissionDisplayName}
                        onChange={function(event) {
                          setEditingGroup({
                            ...editingGroup,
                            permissionDisplayName: event.target.value,
                          });
                        }}
                      />
                      <TextField
                        size="small"
                        variant="outlined"
                        label="Description"
                        value={editingGroup.description}
                        inputProps={{ maxLength: 250 }}
                        onChange={function(event) {
                          setEditingGroup({
                            ...editingGroup,
                            description: event.target.value,
                          });
                        }}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Typography
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#1f2937",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {permission.permissionDisplayName}
                      </Typography>
                      {permission.description ? (
                        <Typography
                          style={{
                            fontSize: 11.5,
                            color: "#6b7280",
                            marginTop: 3,
                          }}
                        >
                          {permission.description}
                        </Typography>
                      ) : null}
                    </Box>
                  )}
                </Typography>
              </Box>
            ) : isEditing ? (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,

                  paddingRight: 10,
                  paddingTop: 4,
                  paddingBottom: 4,
                }}
              >
                <TextField
                  size="small"
                  variant="outlined"
                  label="Permission Display Name"
                  placeholder="Enter display name"
                  value={
                    editingPermission && editingPermission.permissionDisplayName
                      ? editingPermission.permissionDisplayName
                      : ""
                  }
                  onChange={function(event) {
                    handleEditPermissionChange(
                      "permissionDisplayName",
                      event.target.value,
                    );
                  }}
                  fullWidth
                  required
                />

                <TextField
                  size="small"
                  variant="outlined"
                  label="Permission Code"
                  placeholder="e.g. EMPLOYEE_CREATE"
                  value={
                    editingPermission && editingPermission.permissionCode
                      ? editingPermission.permissionCode
                      : ""
                  }
                  onChange={function(event) {
                    handleEditPermissionChange(
                      "permissionCode",
                      event.target.value,
                    );
                  }}
                  fullWidth
                  required
                />

                <TextField
                  size="small"
                  variant="outlined"
                  label="Permission Name"
                  placeholder="e.g. CREATE_EMPLOYEE"
                  value={
                    editingPermission && editingPermission.permissionName
                      ? editingPermission.permissionName
                      : ""
                  }
                  onChange={function(event) {
                    handleEditPermissionChange(
                      "permissionName",
                      event.target.value,
                    );
                  }}
                  fullWidth
                  required
                />

                <TextField
                  size="small"
                  variant="outlined"
                  label="Description"
                  placeholder="Enter permission description"
                  value={
                    editingPermission && editingPermission.description
                      ? editingPermission.description
                      : ""
                  }
                  onChange={function(event) {
                    handleEditPermissionChange(
                      "description",
                      event.target.value,
                    );
                  }}
                  inputProps={{ maxLength: 250 }}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Box>
            ) : (
              <>
                <Typography
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#1f2937",

                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {permission.permissionDisplayName || "-"}
                </Typography>

                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 5,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    size="small"
                    label={"Id: " + (permission.id || "-")}
                    style={{
                      height: 22,
                      fontSize: 10,
                    }}
                  />

                  <Chip
                    size="small"
                    label={"Code: " + (permission.permissionCode || "-")}
                    style={{
                      height: 22,
                      fontSize: 10,
                    }}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    label={"Name: " + (permission.permissionName || "-")}
                    style={{
                      height: 22,
                      fontSize: 10,
                    }}
                  />
                </Box>

                {permission.description ? (
                  <Typography
                    style={{
                      marginTop: 4,
                      fontSize: 11.5,
                      color: "#6b7280",

                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {permission.description}
                  </Typography>
                ) : null}
              </>
            )}
          </Box>

          {isGroup ? (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: 8,
                flexShrink: 0,
              }}
            >
              {isEditingGroup ? (
                <>
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={function() {
                      handleSaveGroup(permission);
                    }}
                    disabled={
                      !editingGroup ||
                      !editingGroup.permissionDisplayName.trim()
                    }
                    style={{ textTransform: "none", marginRight: 5 }}
                  >
                    Save
                  </Button>
                  <IconButton
                    size="small"
                    onClick={function() {
                      setEditingGroupId(null);
                      setEditingGroup(null);
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  size="small"
                  onClick={function() {
                    handleEditGroup(permission);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: 8,
                flexShrink: 0,
              }}
            >
              {isEditing ? (
                <>
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={function() {
                      handleSavePermission(permission);
                    }}
                    disabled={
                      !editingPermission ||
                      !editingPermission.permissionDisplayName ||
                      !editingPermission.permissionCode ||
                      !editingPermission.permissionName
                    }
                    style={{
                      textTransform: "none",
                      marginRight: 5,
                    }}
                  >
                    Save
                  </Button>

                  <IconButton size="small" onClick={handleCancelEditPermission}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  size="small"
                  onClick={function() {
                    handleEditPermission(permission);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={openConnectDialog}
      onClose={function(_, reason) {
        if (reason !== "backdropClick") {
          handleCancel();
        }
      }}
      PaperProps={{
        style: {
          borderRadius: 10,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        className="dialogTitle"
        style={{
          padding: "14px 18px",
        }}
      >
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              style={{
                color: "#ffffff",
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {
              moduleFunctionality.functionalityDisplayName
                ? moduleFunctionality.functionalityDisplayName
                : ""}
              {
              moduleFunctionality.moduleFunctionalityName
                ? " (" + moduleFunctionality.moduleFunctionalityName + ")"
                : ""}
            </Typography>

            <Typography
              style={{
                color: "#ffffff",
                fontSize: 11,
                opacity: 0.8,
                marginTop: 3,
              }}
            >
              {groupCount} Groups • {permissionCount} Permissions
            </Typography>
          </Box>

          <IconButton
            onClick={handleCancel}
            size="small"
            style={{
              color: "#ffffff",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box
        style={{
          padding: "14px 18px",
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddPermissionToTop}
            style={{
              minWidth: 150,
              textTransform: "none",
            }}
          >
            Add Permission
          </Button>

          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={function() {
              setIsGroupFormOpen(true);
              setNewGroupName("");
              setNewGroupDescription("");
            }}
            style={{
              minWidth: 120,
              textTransform: "none",
            }}
          >
            Add Group
          </Button>
        </Box>

        {isGroupFormOpen && (
          <Box
            style={{
              marginTop: 12,
              padding: 12,
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
            }}
          >
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {/* Group Name */}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Group Name"
                placeholder="Enter group name"
                value={newGroupName}
                onChange={function(event) {
                  setNewGroupName(event.target.value);
                }}
                autoFocus
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FolderOutlinedIcon
                        style={{
                          fontSize: 20,
                          color: "#6b7280",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Group Description */}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Group Description"
                placeholder="Enter group description"
                value={newGroupDescription}
                onChange={function(event) {
                  setNewGroupDescription(event.target.value);
                }}
                inputProps={{ maxLength: 250 }}
                multiline
                rows={2}
              />

              {/* Buttons */}
              <Box
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={function() {
                    setNewGroupName("");
                    setNewGroupDescription("");
                    setIsGroupFormOpen(false);
                  }}
                  style={{
                    textTransform: "none",
                    minWidth: 80,
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddGroup}
                  disabled={!newGroupName.trim()}
                  style={{
                    textTransform: "none",
                    minWidth: 80,
                  }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <DialogContent
        style={{
          padding: 16,
          backgroundColor: "#ffffff",
        }}
      >
        {filteredPermissions.length === 0 ? (
          <Box
            style={{
              minHeight: 280,

              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",

              color: "#9ca3af",
            }}
          >
            <FolderOutlinedIcon
              style={{
                fontSize: 44,
                marginBottom: 8,
              }}
            />

            <Typography
              style={{
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {searchText ? "No permissions found" : "No permissions available"}
            </Typography>
          </Box>
        ) : (
          <ReactDragListView {...dragProps}>
            {filteredPermissions.map(renderPermissionRow)}
          </ReactDragListView>
        )}
      </DialogContent>

      {errorMessage && (
        <Box
          style={{
            padding: 16,
            backgroundColor: "#fdecea",
            color: "#b71c1c",
          }}
        >
          <Typography
            style={{
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {errorMessage}
          </Typography>
        </Box>
      )}
      <Divider />

      <DialogActions
        style={{
          padding: "12px 16px",
          backgroundColor: "#fafafa",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          style={{
            minWidth: 90,
            textTransform: "none",
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdate}
          disabled={
            visiblePermissions.length === 0 || editingPermissionId !== null
          }
          style={{
            minWidth: 90,
            textTransform: "none",
          }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
